import { useCallback } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { decideCardPress, isPlainPress } from '@visious/core';
import { useQueueFlow } from './flowContext';
import { readQueue } from './store';
import type { QueueCandidate } from './model';

/**
 * What a press on a video card means while a queue exists.
 *
 * With no queue: nothing at all. The handler returns without touching the event, so the card is the
 * plain `<a href>` it has always been and `Link` navigates. This is the state the app is in for every
 * reader who has never built a queue, and it must cost them nothing — not a question, not a behaviour
 * to learn, not a millisecond.
 *
 * With a queue: the press is claimed and the pressed card asks on its own picture
 * (`QueueCardAsk.tsx`). Opening a video is two different acts once a list is running — drop the list
 * and open the video, or put it next and stay — and the difference between them is a list somebody
 * spent time building.
 *
 * **Every modifier is left to the browser** (`isPlainPress`, core, tested). ⌘/Ctrl-click, Shift-click,
 * Alt-click and the middle button all mean "open this somewhere else", and cancelling one of them to
 * ask a question would take away a habit and give nothing back. `Link` declines the same set for its
 * own navigation, so a modified press on a card behaves identically whether or not a queue exists.
 *
 * The queue is read from the **live snapshot** rather than a subscription: this runs at the instant of
 * a press, and a card that re-rendered forty times a page because a queue changed elsewhere would be
 * paying a render for a question only ever asked once.
 *
 * `null` is a row there is nothing to queue — a playlist entry or a history row whose video is gone.
 * It answers with a handler that declines rather than with no handler, because a hook cannot be
 * called conditionally and a row's *kind* is what decides this.
 */
export function useQueueCardPress(
  item: QueueCandidate | null,
): (event: ReactMouseEvent<HTMLAnchorElement>) => void {
  const flow = useQueueFlow();
  return useCallback(
    (event) => {
      if (flow === null || item === null) return;
      if (!isPlainPress(event)) return;
      if (decideCardPress(readQueue()) !== 'ask') return;
      // Claims the press from `Link`, which declines any event whose default is already prevented.
      event.preventDefault();
      flow.askAbout(item);
    },
    [flow, item],
  );
}
